import React, { useState } from 'react'
import { Grid } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import { useNavigate, useSearchParams } from 'react-router-dom'
import useResetPassword from '../services/hooks/usePostResetPassword'
import useValidation, { ValidationRule } from '@/hooks/useValidation'
import PasswordInput from '@/components/Common/PasswordInput'
import Popup from '@/components/Common/Popup'
import TopNav from '@/components/TopNav/TopNav'
import StyledButton from '@/components/Button/Button'
import StyledBgContainer from '@/components/StyledBgContainer/StyledBgContainer'
import { ResetPasswordPayload } from '../types/APIRequestType'
import Path from '@/router/localRoutes'
import './ChangePassword.css'

const ChangePassword = () => {
  const inputValidationRules: Record<string, ValidationRule[]> = {
    newPassword: [
      { required: true, message: 'Please enter your new password.' },
      {
        pattern: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/,
        message:
          'Password must be at least 8 characters, with uppercase, lowercase letters, and a number.',
      },
    ],
    confirmNewPassword: [
      { required: true, message: 'Please enter your confirm new password.' },
      {
        pattern: (value: string, fields: Record<string, string>) =>
          value === fields.newPassword,
        message: 'New passwords do not match.',
      },
    ],
  }

  const [newPassword, setNewPassword] = useState<string>('')
  const [confirmNewPassword, setConfirmNewPassword] = useState<string>('')
  const [errorMsg, setErrorMsg] = useState<string>('')
  const [showPopup, setShowPopup] = useState<boolean>(false)
  const { errors, validateInputs } = useValidation(
    {
      newPassword,
      confirmNewPassword,
    },
    inputValidationRules,
  )
  const [searchParams] = useSearchParams()

  const token = searchParams.get('token')
  const { mutate: resetPassword, isPending } = useResetPassword()
  const navigate = useNavigate()

  const onChangePassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(event.target.value)
  }

  const onChangeConfirmPassword = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setConfirmNewPassword(event.target.value)
  }

  const handleContinue = () => {
    navigate(Path.Signin)
  }

  const handlePasswordChange = async () => {
    if (!validateInputs()) {
      return
    }
    const requestData: ResetPasswordPayload = {
      newPassword,
      confirmNewPassword,
      token,
    }

    resetPassword(requestData, {
      onSuccess: () => {
        setShowPopup(true)
      },
      onError: (error) => {
        setErrorMsg(error.message || 'Something went wrong. Please try again.')
      },
    })
  }

  return (
    <StyledBgContainer>
      <Grid className="change-password-container">
        <TopNav showArrow={true} />
        <h1 className="heading font-bold">Change Password</h1>
        <p className="w-[420px] my-2 ">
          Your new password must be different from previous password
        </p>
        <div className="change-password-form gap-6 w-[556px]">
          <PasswordInput
            label="Set Password"
            value={newPassword}
            onChange={onChangePassword}
            className="password-field"
            sx={{ width: '556px' }}
            error={
              !!errors.newPassword ||
              errorMsg ===
                'Atleast one uppercase, lowercase, number(0-9), special character'
            }
            helperText={errors.newPassword || errorMsg}
          />
          <PasswordInput
            label="Retype Password"
            value={confirmNewPassword}
            onChange={onChangeConfirmPassword}
            className="password-field"
            sx={{ width: '556px' }}
            error={!!errors.confirmNewPassword}
            helperText={errors.confirmNewPassword || errorMsg}
          />

          {isPending ? (
            <StyledButton className="btn-change-password">
              <CircularProgress className="!text-[#181a21] !text-2xl" />
            </StyledButton>
          ) : (
            <StyledButton
              onClick={handlePasswordChange}
              disabled={!(newPassword && confirmNewPassword)}
              className="btn-change-password !mt-[15vh] !m-auto"
            >
              Change Password
            </StyledButton>
          )}
        </div>
        {showPopup && (
          <Popup
            title="Password Reset Successfull"
            message="Your password has been changed. Use your Email address and new password to access the web portal and mobile application."
            buttonText="Done"
            onButtonClick={handleContinue}
          />
        )}
      </Grid>
    </StyledBgContainer>
  )
}

export default ChangePassword
